/**
 * Atribuição — quanto cada anúncio custou por conversa, por contato novo,
 * por negócio e por venda. Cruza o gasto da Meta com o que caiu no
 * LeadConversa pelo `referral` do webhook.
 */

import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toUserFriendlyError } from "@/lib/error-messages";
import { PermissionGuard } from "@/components/PermissionGuard";
import DashboardLayout from "@/components/DashboardLayout";
import { usePermissions } from "@/hooks/usePermissions";
import { Card, CardContent } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Link } from "wouter";
import { Clock, Handshake, Info, MessageCircle, Trophy, Wallet } from "lucide-react";
import { AdsKpi } from "@/components/ads/AdsKpi";
import { useContaSelecionada } from "@/components/ads/useContaSelecionada";
import { PERIODOS, moeda, numero, type Periodo } from "@/lib/meta-ads-format";

function AdsAttributionContent() {
  const { can } = usePermissions();
  const [periodo, setPeriodo] = useState<Periodo>("last_30d");
  const [porCampanha, setPorCampanha] = useState(false);
  const { accounts, contaSelecionada, setAccountId, currency, conectado } = useContaSelecionada();

  const rel = trpc.metaAds.attribution.report.useQuery(
    { adAccountId: contaSelecionada?.accountId, datePreset: periodo },
    { staleTime: 60_000, enabled: conectado },
  );
  const t = rel.data?.totals;

  if (!conectado && !accounts.isLoading) {
    return (
      <div className="mx-auto max-w-2xl py-16 text-center text-sm text-muted-foreground">
        Conecte a conta da Meta em <strong>Configurações → Integrações → Meta Ads</strong> para ver a atribuição.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center gap-3">
        <div className="mr-auto">
          <h1 className="text-2xl font-semibold">Atribuição de anúncios</h1>
          <p className="text-sm text-muted-foreground">De qual anúncio veio cada conversa, contato e negócio — e quanto custou.</p>
        </div>
        <Select value={contaSelecionada?.accountId ?? ""} onValueChange={setAccountId}>
          <SelectTrigger className="w-[240px]"><SelectValue placeholder="Conta" /></SelectTrigger>
          <SelectContent>
            {(accounts.data ?? []).map((a) => <SelectItem key={a.accountId} value={a.accountId}>{a.name ?? a.accountId}</SelectItem>)}
          </SelectContent>
        </Select>
        {can("ads:credentials_manage") && (
          <Link href="/settings?integration=meta_ads" className="text-xs text-muted-foreground hover:underline" title="Ocultar contas que não usa">
            Gerenciar contas
          </Link>
        )}
        <Select value={periodo} onValueChange={(v) => setPeriodo(v as Periodo)}>
          <SelectTrigger className="w-[170px]"><SelectValue /></SelectTrigger>
          <SelectContent>
            {PERIODOS.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <div className="flex items-start gap-2 rounded border border-blue-200 bg-blue-50 p-3 text-xs text-blue-900 dark:border-blue-900 dark:bg-blue-950/40 dark:text-blue-200">
        <Info className="mt-0.5 h-3.5 w-3.5 shrink-0" />
        <p>
          Anúncios de <strong>clique para o WhatsApp</strong> só entram aqui quando apontam para o número oficial conectado ao LeadConversa —
          anúncio que abre um WhatsApp pessoal não deixa rastro. Os de <strong>Direct</strong> entram sempre.
        </p>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <AdsKpi icon={<Wallet className="h-4 w-4" />} label="Gasto" value={moeda(t?.spendCents, currency)} hint={rel.data ? `${rel.data.since} → ${rel.data.until}` : undefined} />
        <AdsKpi icon={<MessageCircle className="h-4 w-4" />} label="Conversas" value={numero(t?.conversas)} hint={t?.custoPorConversaCents != null ? `${moeda(t.custoPorConversaCents, currency)} por conversa` : "—"} />
        <AdsKpi icon={<Handshake className="h-4 w-4" />} label="Negócios" value={numero(t?.negocios)} hint={t?.custoPorNegocioCents != null ? `${moeda(t.custoPorNegocioCents, currency)} por negócio` : `${numero(t?.contatos)} contato(s) novo(s)`} />
        <AdsKpi icon={<Trophy className="h-4 w-4" />} label="Vendas" value={numero(t?.ganhos)} hint={t?.custoPorVendaCents != null ? `${moeda(t.custoPorVendaCents, currency)} por venda · ${moeda(t.valorGanhoCents, currency)} ganhos` : "—"} />
      </div>

      {rel.data?.timezoneName && (
        <p className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Clock className="h-3 w-3" /> Período no fuso da conta ({rel.data.timezoneName}). Negócios contam pela data de criação; "vendas" são os criados no período que hoje estão ganhos.
        </p>
      )}

      <Card>
        <CardContent className="pt-6">
          <div className="mb-3 flex items-center justify-between">
            <h2 className="font-medium">{porCampanha ? "Por campanha" : "Por anúncio"}</h2>
            <label className="flex items-center gap-2 text-xs">
              <Switch checked={porCampanha} onCheckedChange={setPorCampanha} /> Agrupar por campanha
            </label>
          </div>
          {rel.isLoading && <p className="text-sm text-muted-foreground">Cruzando gasto e conversas…</p>}
          {rel.isError && <p className="text-sm text-red-600">{toUserFriendlyError(rel.error)}</p>}
          {rel.data && rel.data.ads.length === 0 && <p className="text-sm text-muted-foreground">Nenhum anúncio com gasto ou conversas no período.</p>}
          {rel.data && rel.data.ads.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[900px] text-sm">
                <thead>
                  <tr className="border-b text-left text-xs uppercase text-muted-foreground">
                    <th className="pb-2 pr-3 font-medium">{porCampanha ? "Campanha" : "Anúncio"}</th>
                    <th className="pb-2 pr-3 text-right font-medium">Gasto</th>
                    <th className="pb-2 pr-3 text-right font-medium">Cliques</th>
                    <th className="pb-2 pr-3 text-right font-medium">Conversas</th>
                    <th className="pb-2 pr-3 text-right font-medium">Contatos novos</th>
                    <th className="pb-2 pr-3 text-right font-medium">Negócios</th>
                    <th className="pb-2 pr-3 text-right font-medium">Vendas</th>
                    <th className="pb-2 pr-3 text-right font-medium">Custo/conversa</th>
                    <th className="pb-2 pr-3 text-right font-medium">Custo/negócio</th>
                    <th className="pb-2 text-right font-medium">Custo/venda</th>
                  </tr>
                </thead>
                <tbody>
                  {(porCampanha ? rel.data.campanhas : rel.data.ads).map((l, i) => {
                    const semInsight = "semInsight" in l && l.semInsight;
                    const nome = porCampanha
                      ? (l as (typeof rel.data.campanhas)[number]).campaignName ?? (l as (typeof rel.data.campanhas)[number]).campaignId ?? "—"
                      : (l as (typeof rel.data.ads)[number]).adName ?? (l as (typeof rel.data.ads)[number]).adId;
                    const campaignId = l.campaignId;
                    return (
                      <tr key={i} className={`border-b last:border-0 ${semInsight ? "text-muted-foreground" : ""}`}>
                        <td className="max-w-[360px] py-2 pr-3">
                          {campaignId ? <Link href={`/ads/campaigns/${campaignId}`} className="line-clamp-2 hover:underline">{nome}</Link> : <span className="line-clamp-2">{nome}</span>}
                          {!porCampanha && (l as (typeof rel.data.ads)[number]).campaignName && <span className="block truncate text-xs text-muted-foreground">{(l as (typeof rel.data.ads)[number]).campaignName}</span>}
                          {semInsight && <span className="block text-[11px] text-muted-foreground">sem gasto no período (anúncio antigo)</span>}
                          {porCampanha && <span className="block text-[11px] text-muted-foreground">{(l as (typeof rel.data.campanhas)[number]).anuncios} anúncio(s)</span>}
                        </td>
                        <td className="py-2 pr-3 text-right">{moeda(l.spendCents, currency)}</td>
                        <td className="py-2 pr-3 text-right">{numero(l.clicks)}</td>
                        <td className="py-2 pr-3 text-right font-medium">{numero(l.conversas)}</td>
                        <td className="py-2 pr-3 text-right">{numero(l.contatos)}</td>
                        <td className="py-2 pr-3 text-right">{numero(l.negocios)}</td>
                        <td className="py-2 pr-3 text-right">{numero(l.ganhos)}{l.valorGanhoCents > 0 && <span className="ml-1 text-xs text-muted-foreground">{moeda(l.valorGanhoCents, currency)}</span>}</td>
                        <td className="py-2 pr-3 text-right">{moeda(l.custoPorConversaCents, currency)}</td>
                        <td className="py-2 pr-3 text-right">{moeda(l.custoPorNegocioCents, currency)}</td>
                        <td className="py-2 text-right">{moeda(l.custoPorVendaCents, currency)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
          {rel.data?.truncado && <p className="mt-2 text-xs text-muted-foreground">A conta tem mais anúncios do que o relatório mostra; o gasto acima é parcial.</p>}
        </CardContent>
      </Card>
    </div>
  );
}

export default function AdsAttribution() {
  return (
    <PermissionGuard permission="ads:view">
      <DashboardLayout>
        <AdsAttributionContent />
      </DashboardLayout>
    </PermissionGuard>
  );
}
